import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Globe,
  Search,
  Plus,
  Users,
  ArrowLeft,
  Building2,
  MapPin,
  Mail,
  Phone,
  Linkedin,
  Target,
  X,
  Edit,
  Trash2,
  Filter,
  Star,
  Menu
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TagInput = ({ tags, onChange, suggestions = [] }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredSuggestions = suggestions.filter(
    s => s.toLowerCase().includes(inputValue.toLowerCase()) && !tags.includes(s)
  );

  const addTag = (tag) => {
    if (tag && !tags.includes(tag)) {
      onChange([...tags, tag]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove) => {
    onChange(tags.filter(t => t !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      addTag(inputValue);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="tag flex items-center gap-1"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-red-500"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder="Type and press Enter to add tag"
          data-testid="future-tag-input"
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => addTag(suggestion)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FutureNetwork = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tierFilter, setTierFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    job_title: '',
    current_employer: '',
    designation: '',
    city_based_in: '',
    email_address: '',
    phone_numbers: {
      handphone: { country_code: '', number: '' },
      work: { country_code: '', number: '' }
    },
    linkedin_url: '',
    angle: '',
    tier: '',
    custom_tags: []
  });

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/future-network`, getAuthHeader());
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching future contacts:', error);
      toast.error('Failed to load future network');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tags`, getAuthHeader());
      setTagSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchContacts();
    fetchTags();
  }, [fetchContacts, fetchTags]);

  // Search with filters
  useEffect(() => {
    const timer = setTimeout(async () => {
      // Only search if there's a query or filter active
      const hasTierFilter = tierFilter && tierFilter !== 'all';
      if (searchQuery.length >= 2 || hasTierFilter || cityFilter.length >= 2) {
        try {
          const params = new URLSearchParams();
          if (searchQuery.length >= 2) params.append('q', searchQuery);
          if (hasTierFilter) params.append('tier', tierFilter);
          if (cityFilter.length >= 2) params.append('city', cityFilter);
          
          const response = await axios.get(
            `${API_URL}/api/future-network-search?${params.toString()}`,
            getAuthHeader()
          );
          setSearchResults(response.data);
        } catch (error) {
          console.error('Search error:', error);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, tierFilter, cityFilter, getAuthHeader]);

  const resetForm = () => {
    setFormData({
      name: '',
      job_title: '',
      current_employer: '',
      designation: '',
      city_based_in: '',
      email_address: '',
      phone_numbers: {
        handphone: { country_code: '', number: '' },
        work: { country_code: '', number: '' }
      },
      linkedin_url: '',
      angle: '',
      tier: '',
      custom_tags: []
    });
  };

  const handleCreateContact = async () => {
    try {
      await axios.post(`${API_URL}/api/future-network`, formData, getAuthHeader());
      toast.success('Future contact added');
      fetchContacts();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating future contact:', error);
      toast.error('Failed to add contact');
    }
  };

  const handleUpdateContact = async () => {
    try {
      const response = await axios.put(
        `${API_URL}/api/future-network/${editingContact.id}`,
        formData,
        getAuthHeader()
      );
      toast.success('Contact updated');
      fetchContacts();
      setSelectedContact(response.data);
      setShowForm(false);
      setEditingContact(null);
      resetForm();
    } catch (error) {
      console.error('Error updating future contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/future-network/${contactToDelete.id}`,
        getAuthHeader()
      );
      toast.success('Contact deleted');
      fetchContacts();
      if (selectedContact?.id === contactToDelete.id) {
        setSelectedContact(null);
      }
      setShowDeleteDialog(false);
      setContactToDelete(null);
    } catch (error) {
      console.error('Error deleting future contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const openEditForm = (contact) => {
    setFormData({
      name: contact.name || '',
      job_title: contact.job_title || '',
      current_employer: contact.current_employer || '',
      designation: contact.designation || '',
      city_based_in: contact.city_based_in || '',
      email_address: contact.email_address || '',
      phone_numbers: contact.phone_numbers || {
        handphone: { country_code: '', number: '' },
        work: { country_code: '', number: '' }
      },
      linkedin_url: contact.linkedin_url || '',
      angle: contact.angle || '',
      tier: contact.tier || '',
      custom_tags: contact.custom_tags || []
    });
    setEditingContact(contact);
    setShowForm(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePhoneNumber = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      phone_numbers: {
        ...prev.phone_numbers,
        [type]: {
          ...prev.phone_numbers[type],
          [field]: value
        }
      }
    }));
  };

  const formatPhone = (phone) => {
    if (!phone || !phone.number) return null;
    return `+${phone.country_code || ''} ${phone.number}`;
  };

  const displayContacts = (searchQuery.length >= 2 || (tierFilter && tierFilter !== 'all') || cityFilter.length >= 2) ? searchResults : contacts;

  const clearFilters = () => {
    setSearchQuery('');
    setTierFilter('');
    setCityFilter('');
    setSearchResults([]);
  };

  const hasActiveFilters = searchQuery || (tierFilter && tierFilter !== 'all') || cityFilter;

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`w-80 bg-[#0F172A] flex flex-col h-screen fixed left-0 top-0 z-50 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Contacts
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-white">Future Network</h1>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b border-slate-700 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search future contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="future-search-input"
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Filter Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="toggle-filters"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && <span className="ml-auto w-2 h-2 rounded-full bg-[#D4AF37]"></span>}
          </Button>
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="space-y-3 pt-2 animate-fadeIn">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wide mb-1 block">Tier</Label>
                <Select value={tierFilter} onValueChange={setTierFilter}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="tier-filter">
                    <SelectValue placeholder="All Tiers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="tier_1">Tier 1</SelectItem>
                    <SelectItem value="tier_2">Tier 2</SelectItem>
                    <SelectItem value="tier_3">Tier 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wide mb-1 block">City</Label>
                <Input
                  placeholder="Filter by city..."
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  data-testid="city-filter"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                />
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="clear-filters"
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Contact List */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin"></div>
              </div>
            ) : displayContacts.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">
                  {hasActiveFilters ? 'No contacts match filters' : 'No future contacts yet'}
                </p>
              </div>
            ) : (
              displayContacts.map((contact, index) => (
                <button
                  key={contact.id}
                  onClick={() => {
                    setSelectedContact(contact);
                    setShowForm(false);
                    setSidebarOpen(false);
                  }}
                  data-testid={`future-contact-item-${index}`}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-all contact-item ${
                    selectedContact?.id === contact.id 
                      ? 'active bg-[#D4AF37]/15 border-l-3 border-[#D4AF37]' 
                      : 'hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium truncate flex-1">{contact.name}</p>
                    {contact.tier && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${
                        contact.tier === 'tier_1' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                        contact.tier === 'tier_2' ? 'bg-slate-600 text-slate-300' :
                        'bg-slate-700 text-slate-400'
                      }`}>
                        {contact.tier === 'tier_1' ? 'T1' : contact.tier === 'tier_2' ? 'T2' : 'T3'}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-sm truncate">
                    {contact.current_employer || 'No employer'}
                  </p>
                </button>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Add Button */}
        <div className="p-4 border-t border-slate-700">
          <Button
            onClick={() => {
              resetForm();
              setEditingContact(null);
              setShowForm(true);
              setSelectedContact(null);
            }}
            className="w-full gap-2 bg-[#D4AF37] hover:bg-[#B08D28] text-white"
            data-testid="add-future-contact-button"
          >
            <Plus className="w-4 h-4" />
            Add Future Contact
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-80 min-h-screen">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-[#0F172A]"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className="text-lg sm:text-2xl font-heading font-semibold text-[#0F172A] truncate">
              {showForm 
                ? (editingContact ? 'Edit Future Contact' : 'Add Future Contact')
                : selectedContact 
                  ? selectedContact.name 
                  : 'Future Network'
              }
            </h2>
            </div>
            {selectedContact && !showForm && (
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditForm(selectedContact)}
                  className="gap-2"
                  data-testid="edit-future-contact-button"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setContactToDelete(selectedContact);
                    setShowDeleteDialog(true);
                  }}
                  className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-future-contact-button"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-8">
          {showForm ? (
            /* Form */
            <Card className="max-w-2xl animate-fadeIn">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="Full name"
                      data-testid="future-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Current Employer</Label>
                    <Input
                      value={formData.current_employer}
                      onChange={(e) => updateField('current_employer', e.target.value)}
                      placeholder="Company name"
                      data-testid="future-employer-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Job Title</Label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) => updateField('job_title', e.target.value)}
                      placeholder="e.g., Managing Director"
                      data-testid="future-jobtitle-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Designation</Label>
                    <Input
                      value={formData.designation}
                      onChange={(e) => updateField('designation', e.target.value)}
                      placeholder="e.g., Partner"
                      data-testid="future-designation-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">City Based In</Label>
                    <Input
                      value={formData.city_based_in}
                      onChange={(e) => updateField('city_based_in', e.target.value)}
                      placeholder="e.g., Singapore"
                      data-testid="future-city-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Email</Label>
                    <Input
                      type="email"
                      value={formData.email_address}
                      onChange={(e) => updateField('email_address', e.target.value)}
                      placeholder="email@company.com"
                      data-testid="future-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Handphone</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.phone_numbers.handphone.country_code}
                        onChange={(e) => updatePhoneNumber('handphone', 'country_code', e.target.value)}
                        placeholder="+65"
                        className="w-20"
                        data-testid="future-handphone-code"
                      />
                      <Input
                        value={formData.phone_numbers.handphone.number}
                        onChange={(e) => updatePhoneNumber('handphone', 'number', e.target.value)}
                        placeholder="9123 4567"
                        className="flex-1"
                        data-testid="future-handphone-number"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Work Phone</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.phone_numbers.work.country_code}
                        onChange={(e) => updatePhoneNumber('work', 'country_code', e.target.value)}
                        placeholder="+65"
                        className="w-20"
                        data-testid="future-work-code"
                      />
                      <Input
                        value={formData.phone_numbers.work.number}
                        onChange={(e) => updatePhoneNumber('work', 'number', e.target.value)}
                        placeholder="6789 0123"
                        className="flex-1"
                        data-testid="future-work-number"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">LinkedIn Profile URL</Label>
                  <Input
                    value={formData.linkedin_url}
                    onChange={(e) => updateField('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    data-testid="future-linkedin-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Angle</Label>
                  <Input
                    value={formData.angle}
                    onChange={(e) => updateField('angle', e.target.value)}
                    placeholder="Why/how do you want to connect with this person?"
                    data-testid="future-angle-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Tier</Label>
                  <RadioGroup
                    value={formData.tier}
                    onValueChange={(value) => updateField('tier', value)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="tier_1" id="tier-1" data-testid="tier-1" />
                      <Label htmlFor="tier-1" className="cursor-pointer flex items-center gap-1">
                        <Star className="w-4 h-4 text-[#D4AF37]" />
                        Tier 1
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="tier_2" id="tier-2" data-testid="tier-2" />
                      <Label htmlFor="tier-2" className="cursor-pointer">Tier 2</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="tier_3" id="tier-3" data-testid="tier-3" />
                      <Label htmlFor="tier-3" className="cursor-pointer">Tier 3</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Custom Tags</Label>
                  <TagInput
                    tags={formData.custom_tags}
                    onChange={(tags) => updateField('custom_tags', tags)}
                    suggestions={tagSuggestions}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingContact(null);
                      resetForm();
                    }}
                    data-testid="cancel-future-form"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={editingContact ? handleUpdateContact : handleCreateContact}
                    disabled={!formData.name}
                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
                    data-testid="submit-future-form"
                  >
                    {editingContact ? 'Update Contact' : 'Save Contact'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : selectedContact ? (
            /* Contact Profile */
            <div className="space-y-6 animate-fadeIn">
              <Card className="border-l-4 border-l-[#D4AF37]">
                <CardContent className="pt-6">
                  <h1 className="text-3xl font-heading font-semibold text-[#0F172A] mb-2">
                    {selectedContact.name}
                  </h1>
                  {selectedContact.job_title && (
                    <p className="text-lg text-slate-600 mb-1">{selectedContact.job_title}</p>
                  )}
                  {selectedContact.designation && (
                    <p className="text-slate-500">{selectedContact.designation}</p>
                  )}
                  
                  {/* Tier Badge */}
                  {selectedContact.tier && (
                    <div className="mt-3">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                        selectedContact.tier === 'tier_1' ? 'bg-[#D4AF37]/20 text-[#8A7120]' :
                        selectedContact.tier === 'tier_2' ? 'bg-slate-200 text-slate-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {selectedContact.tier === 'tier_1' && <Star className="w-4 h-4" />}
                        {selectedContact.tier === 'tier_1' ? 'Tier 1' : selectedContact.tier === 'tier_2' ? 'Tier 2' : 'Tier 3'}
                      </span>
                    </div>
                  )}
                  
                  {selectedContact.custom_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {selectedContact.custom_tags.map((tag, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-[#D4AF37]/10 text-[#8A7120] border-[#D4AF37]/30"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                      <Building2 className="w-4 h-4 text-[#D4AF37]" />
                      Contact Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedContact.current_employer && (
                      <div className="flex items-start gap-3">
                        <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">Employer</p>
                          <p className="text-slate-700">{selectedContact.current_employer}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedContact.city_based_in && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">City</p>
                          <p className="text-slate-700">{selectedContact.city_based_in}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedContact.email_address && (
                      <div className="flex items-start gap-3">
                        <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">Email</p>
                          <a 
                            href={`mailto:${selectedContact.email_address}`}
                            className="text-[#D4AF37] hover:underline"
                          >
                            {selectedContact.email_address}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {formatPhone(selectedContact.phone_numbers?.handphone) && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">Handphone</p>
                          <p className="text-slate-700">{formatPhone(selectedContact.phone_numbers.handphone)}</p>
                        </div>
                      </div>
                    )}
                    
                    {formatPhone(selectedContact.phone_numbers?.work) && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">Work</p>
                          <p className="text-slate-700">{formatPhone(selectedContact.phone_numbers.work)}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedContact.linkedin_url && (
                      <div className="flex items-start gap-3">
                        <Linkedin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">LinkedIn</p>
                          <a 
                            href={selectedContact.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4AF37] hover:underline"
                          >
                            View Profile
                          </a>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {selectedContact.angle && (
                  <Card className="border-[#D4AF37]/30">
                    <CardHeader>
                      <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                        <Target className="w-4 h-4 text-[#D4AF37]" />
                        Connection Angle
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600">{selectedContact.angle}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-[60vh] text-center">
              {contacts.length > 0 ? (
                <>
                  <Globe className="w-16 h-16 text-slate-200 mb-6" />
                  <h3 className="text-2xl font-heading font-semibold text-[#0F172A] mb-2">
                    Select a Contact
                  </h3>
                  <p className="text-slate-500 max-w-md">
                    Choose a future contact from the sidebar to view details, or add a new one.
                  </p>
                </>
              ) : (
                <>
                  <div 
                    className="w-64 h-64 mb-8 rounded-2xl bg-cover bg-center opacity-50"
                    style={{
                      backgroundImage: `url('https://images.unsplash.com/photo-1707047754373-ba65fd3f249f?crop=entropy&cs=srgb&fm=jpg&q=85')`
                    }}
                  ></div>
                  <h3 className="text-2xl font-heading font-semibold text-[#0F172A] mb-2">
                    Future Network
                  </h3>
                  <p className="text-slate-500 max-w-md mb-6">
                    Track people you want to connect with. Add potential contacts, note your connection angle, and build your future network.
                  </p>
                  <Button
                    onClick={() => {
                      resetForm();
                      setShowForm(true);
                    }}
                    data-testid="add-first-future-contact"
                    className="gap-2 bg-[#D4AF37] hover:bg-[#B08D28] text-white"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your First Future Contact
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Future Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {contactToDelete?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-future">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContact}
              data-testid="confirm-delete-future"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FutureNetwork;
